import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Search, X, Loader2, Check } from 'lucide-react';
import { Combobox } from './Combobox';
import { useDebounce } from '../../../../hooks/useDebounce';

export interface MultiSelectItem {
  id: string;
  /** Codice principale (SKU per articolo, ID/code per macchina). */
  code: string;
  /** Marca. */
  brand: string;
  /** Nome / modello / descrizione. */
  primary: string;
  /** Secondaria opzionale (categoria articolo, tipologia macchina). */
  secondary?: string;
  /** Giacenza disponibile (opzionale — mostrata come badge). */
  stock?: number;
  /** Percentuale sconto attiva (0-100). null = nessuno sconto. */
  discountPercent?: number | null;
  /** Data scadenza sconto in formato ISO. null = sconto fisso (senza scadenza). */
  discountExpiry?: string | null;
  /** Numero di associazioni esistenti (opzionale — colonna "ASSOC."). */
  assocCount?: number;
  /** Data ISO dell'ultima associazione creata (opzionale). null = nessuna. */
  lastAssocDate?: string | null;
}

const formatAssocDate = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

export interface PagedFetch {
  items: MultiSelectItem[];
  total: number;
}

interface BrandFilterCfg {
  value: string | null;
  onChange: (v: string | null) => void;
  options: { code: string; label: string }[];
  testId?: string;
  placeholder?: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  title: string;
  /** Etichetta del bottone di conferma (es. "Aggiungi N articoli"). */
  confirmLabel: (count: number) => string;
  /**
   * Fetcher paginato. Riceve ``page`` (0-based) e il testo della ricerca.
   * Il parametro ``brand`` è passato solo se il dialog ha `brandFilter`.
   */
  fetcher: (params: {
    search: string;
    brand?: string;
    page: number;
    pageSize: number;
  }) => Promise<PagedFetch>;
  /** IDs già nella matrice — vengono mostrati ma disabilitati. */
  alreadyIn: Set<string>;
  alreadyInLabel?: string;
  searchPlaceholder?: string;
  /** Se presente, mostra Combobox marca a sinistra del campo di ricerca. */
  brandFilter?: BrandFilterCfg;
  onConfirm: (items: MultiSelectItem[]) => void;
  testIdPrefix: string;
  pageSize?: number;
  /** Slot per filtri aggiuntivi (es. categoria, solo disponibili). */
  extraFilters?: React.ReactNode;
  /** 'multi' (default): checkbox + conferma. 'single': click su riga seleziona subito. */
  selectionMode?: 'multi' | 'single';
  /** Richiesto quando selectionMode='single': invocato al click sulla riga. */
  onPickOne?: (item: MultiSelectItem) => void;
  /** Mostra la colonna CODICE (default true). */
  showCode?: boolean;
  /** Mostra le colonne ASSOC. / ULTIMA ASSOC. (default false). */
  showAssocInfo?: boolean;
}

/**
 * Dialog di selezione multipla con ricerca debounced, infinite scroll e
 * (opzionale) filtro marca posto a sinistra del campo testuale.
 * UI ispirata al dialog "vedi" delle pagine Articoli/Macchine.
 */
export default function MultiSelectDialog({
  open, onClose, title, confirmLabel,
  fetcher, alreadyIn, alreadyInLabel = 'già presente',
  searchPlaceholder = 'cerca per marca, codice o descrizione…',
  brandFilter, onConfirm, testIdPrefix, pageSize = 50, extraFilters,
  selectionMode = 'multi', onPickOne, showCode = true, showAssocInfo = false,
}: Props) {
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebounce(query, 300);
  const [items, setItems] = useState<MultiSelectItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const scrollRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  // Reset on open/close
  useEffect(() => {
    if (!open) {
      setQuery(''); setItems([]); setTotal(0); setPage(0); setSelected(new Set());
    }
  }, [open]);

  // Reset list when search or brand cambia
  useEffect(() => {
    if (!open) return;
    setItems([]); setTotal(0); setPage(0);
  }, [debouncedQuery, brandFilter?.value, open]);

  // Fetch when page/search/brand change
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    fetcher({
      search: debouncedQuery,
      brand: brandFilter?.value ?? undefined,
      page,
      pageSize,
    })
      .then(r => {
        setItems(prev => page === 0 ? r.items : [...prev, ...r.items]);
        setTotal(r.total);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, page, debouncedQuery, brandFilter?.value]);

  // Infinite scroll via IntersectionObserver
  useEffect(() => {
    const root = scrollRef.current;
    const sentinel = sentinelRef.current;
    if (!root || !sentinel || !open) return;
    const observer = new IntersectionObserver((entries) => {
      const e = entries[0];
      if (e.isIntersecting && !loading && items.length < total) {
        setPage(p => p + 1);
      }
    }, { root, threshold: 0.1 });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [loading, items.length, total, open]);

  const selectableItems = useMemo(
    () => items.filter(it => !alreadyIn.has(it.id)),
    [items, alreadyIn],
  );

  const allSelected = selectableItems.length > 0 &&
    selectableItems.every(it => selected.has(it.id));

  const toggleOne = (id: string) => {
    const s = new Set(selected);
    if (s.has(id)) s.delete(id); else s.add(id);
    setSelected(s);
  };

  const toggleAll = () => {
    if (allSelected) {
      const s = new Set(selected);
      selectableItems.forEach(it => s.delete(it.id));
      setSelected(s);
    } else {
      const s = new Set(selected);
      selectableItems.forEach(it => s.add(it.id));
      setSelected(s);
    }
  };

  const handleConfirm = () => {
    if (selected.size === 0) return;
    const sel = items.filter(it => selected.has(it.id));
    onConfirm(sel);
  };

  const handleRowClick = (it: MultiSelectItem) => {
    if (selectionMode === 'single') { onPickOne?.(it); return; }
    toggleOne(it.id);
  };

  // Colonne dinamiche in base a selectionMode / showCode / showAssocInfo.
  const gridCols: string[] = [];
  if (selectionMode === 'multi') gridCols.push('34px');
  if (showCode) gridCols.push('100px');
  gridCols.push('minmax(0,260px)');
  gridCols.push('120px');
  if (showAssocInfo) { gridCols.push('90px'); gridCols.push('130px'); }
  gridCols.push('minmax(0,1fr)'); // filler: assorbe lo spazio residuo, evita che NOME si allarghi troppo
  const gridStyle = { gridTemplateColumns: gridCols.join(' ') };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      data-testid={`${testIdPrefix}-dialog`}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-[11px] shadow-2xl max-w-3xl w-full h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex-none px-6 py-4 border-b border-[#eee]">
          <div className="flex items-start gap-3 mb-3">
            <div className="flex-1 min-w-0">
              <div className="text-[17px] font-semibold text-[#1f1f1d]" data-testid={`${testIdPrefix}-title`}>
                {title}
              </div>
              <div className="text-[13px] font-mono text-[#5c5c57] mt-0.5">
                {total} risultati · {items.length} caricati
                {selectionMode === 'multi' && (
                  <> · <span className="text-[#2b3a8f] font-semibold">{selected.size} selezionati</span></>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              data-testid={`${testIdPrefix}-close`}
              className="text-[#5c5c57] hover:text-[#5c5c57] flex-none"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Brand filter (opz.) + Search */}
          <div className="flex items-center gap-2">
            {brandFilter && (
              <div className="w-44 flex-none">
                <Combobox
                  value={brandFilter.value}
                  onChange={brandFilter.onChange}
                  options={brandFilter.options}
                  placeholder={brandFilter.placeholder ?? 'Tutte le marche'}
                  testId={brandFilter.testId ?? `${testIdPrefix}-brand`}
                />
              </div>
            )}
            <div className="flex-1 flex items-center gap-2 px-2.5 py-[7px] border border-[#d8d8d4] rounded-[7px] bg-[#fafafa]">
              <Search className="w-3.5 h-3.5 text-[#5c5c57] flex-none" />
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={searchPlaceholder}
                data-testid={`${testIdPrefix}-search`}
                className="flex-1 text-[14px] outline-none bg-transparent placeholder:text-[#6d6d68]"
              />
              {query && (
                <button onClick={() => setQuery('')} className="text-[#5c5c57] hover:text-[#5c5c57]" data-testid={`${testIdPrefix}-search-clear`}>
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
          </div>
          {/* Filtri aggiuntivi (slot opzionale) */}
          {extraFilters && (
            <div className="mt-2">
              {extraFilters}
            </div>
          )}
        </div>

        {/* Column header con select-all */}
        <div className="flex-none px-6 py-2 bg-[#fafafa] border-b border-[#e4e4e1] grid gap-3 items-center divide-x divide-[#e2e2df]" style={gridStyle}>
          {selectionMode === 'multi' && (
            <div className="text-center">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={toggleAll}
                disabled={selectableItems.length === 0}
                data-testid={`${testIdPrefix}-select-all`}
              />
            </div>
          )}
          {showCode && <div className="text-[11px] font-bold tracking-[0.08em] text-[#5c5c57] font-mono">CODICE</div>}
          <div className="text-[11px] font-bold tracking-[0.08em] text-[#5c5c57] font-mono">NOME</div>
          <div className="text-[11px] font-bold tracking-[0.08em] text-[#5c5c57] font-mono">MARCA</div>
          {showAssocInfo && (
            <>
              <div className="text-[11px] font-bold tracking-[0.08em] text-[#5c5c57] font-mono text-right">ASSOC.</div>
              <div className="text-[11px] font-bold tracking-[0.08em] text-[#5c5c57] font-mono text-right">ULTIMA ASSOC.</div>
            </>
          )}
        </div>

        {/* List */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto" data-testid={`${testIdPrefix}-list`}>
          {items.length === 0 && !loading ? (
            <div className="px-6 py-10 text-center text-[14px] text-[#5c5c57]" data-testid={`${testIdPrefix}-empty`}>
              {query.length > 0 || brandFilter?.value
                ? `Nessun risultato${query ? ` per "${query}"` : ''}${brandFilter?.value ? ` (marca filtrata)` : ''}`
                : 'Digita per cercare o seleziona una marca.'}
            </div>
          ) : (
            <>
              {items.map((it, i) => {
                const isAlready = alreadyIn.has(it.id);
                const isSel = selected.has(it.id);
                return (
                  <div
                    key={`${it.id}-${i}`}
                    onClick={() => !isAlready && handleRowClick(it)}
                    data-testid={`${testIdPrefix}-row-${it.id}`}
                    className={`px-6 py-2.5 border-b border-[#f1f1ee] grid gap-3 items-center cursor-pointer divide-x divide-[#eeeeeb] ${
                      isAlready ? 'opacity-50 cursor-not-allowed bg-[#fafafa]' :
                        isSel ? 'bg-[#eef1fb]' : 'hover:bg-[#fafbff]'
                    }`}
                    style={gridStyle}
                  >
                    {selectionMode === 'multi' && (
                      <div className="text-center">
                        {isAlready ? (
                          <span className="text-[12px] text-[#5c5c57]" title={alreadyInLabel}><Check className="w-3 h-3 mx-auto text-[#5c5c57]" /></span>
                        ) : (
                          <input
                            type="checkbox"
                            checked={isSel}
                            onChange={() => toggleOne(it.id)}
                            onClick={(e) => e.stopPropagation()}
                            data-testid={`${testIdPrefix}-row-check-${it.id}`}
                          />
                        )}
                      </div>
                    )}
                    {showCode && <div className="text-[14px] font-mono font-semibold text-[#1f1f1d] truncate">{it.code}</div>}
                    <div className="text-[14px] text-[#3a3a36] min-w-0">
                      <div className="truncate font-bold text-[#1f1f1d]" title={it.primary}>{it.primary}</div>
                      {it.secondary && (
                        <div className="text-[12px] font-mono text-[#5c5c57] truncate font-normal">{it.secondary}</div>
                      )}
                      {/* Badge stock + sconto */}
                      {(it.stock !== undefined || (it.discountPercent != null && it.discountPercent > 0)) && (
                        <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                          {it.stock !== undefined && (
                            <span className={`inline-flex items-center text-[11px] font-mono px-1.5 py-0.5 rounded font-semibold ${
                              it.stock > 0 ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-500'
                            }`}>
                              {it.stock > 0 ? `${it.stock} disp.` : 'esaurito'}
                            </span>
                          )}
                          {it.discountPercent != null && it.discountPercent > 0 && (
                            <span className="inline-flex items-center text-[11px] font-mono px-1.5 py-0.5 rounded font-semibold bg-amber-50 text-amber-700">
                              -{it.discountPercent}%
                              {it.discountExpiry
                                ? ` · scade ${new Date(it.discountExpiry).toLocaleDateString('it-IT', { day: '2-digit', month: 'short' })}`
                                : ' · fisso'}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="text-[14px] text-[#5c5c57] truncate">{it.brand || '—'}</div>
                    {showAssocInfo && (
                      <>
                        <div className="text-[14px] font-mono font-semibold text-[#2b3a8f] text-right">{it.assocCount ?? 0}</div>
                        <div className="text-[13px] font-mono text-[#5c5c57] truncate text-right">{formatAssocDate(it.lastAssocDate)}</div>
                      </>
                    )}
                  </div>
                );
              })}
              {/* Sentinel for infinite scroll */}
              <div ref={sentinelRef} className="h-8 flex items-center justify-center">
                {loading && <Loader2 className="w-4 h-4 animate-spin text-[#5c5c57]" />}
                {!loading && items.length >= total && total > 0 && (
                  <span className="text-[12px] font-mono text-[#5c5c57]">— fine elenco —</span>
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex-none px-6 py-4 border-t border-[#eee] flex items-center gap-3">
          {selectionMode === 'multi' && (
            <>
              <span className="text-[14px] text-[#3a3a36]">
                <strong className="text-[#2b3a8f]">{selected.size}</strong> selezionati
              </span>
              {selected.size > 0 && (
                <button
                  onClick={() => setSelected(new Set())}
                  className="text-[13px] text-[#5c5c57] hover:underline"
                  data-testid={`${testIdPrefix}-clear`}
                >
                  azzera
                </button>
              )}
            </>
          )}
          <div className="flex-1" />
          <button
            onClick={onClose}
            data-testid={`${testIdPrefix}-cancel`}
            className="px-3 py-2 text-[14px] text-[#5c5c57] hover:bg-[#fafafa] rounded-[7px]"
          >
            Annulla
          </button>
          {selectionMode === 'multi' && (
            <button
              onClick={handleConfirm}
              disabled={selected.size === 0}
              data-testid={`${testIdPrefix}-confirm`}
              className="px-4 py-2 rounded-[7px] bg-[#2b3a8f] text-white text-[14px] font-semibold hover:bg-[#243279] disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {confirmLabel(selected.size)}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
